import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type { LlmRequest } from '../../core/types/data-models';
import { didUseWebSearch } from './promptMonitoring.utils';

interface LlmRequestDetailDialogProps {
  open: boolean;
  request: LlmRequest | null;
  projectName?: string;
  onClose: () => void;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function PromptBlock({ label, content, copyable }: { label: string; content: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {copyable && (
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box
        component="pre"
        sx={{
          mt: 0.5,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 280,
          overflowY: 'auto',
        }}
      >
        {content}
      </Box>
    </Box>
  );
}

/** Read-only view of a single LLM request audit record — no editing. */
export function LlmRequestDetailDialog({ open, request, projectName, onClose }: LlmRequestDetailDialogProps) {
  if (!request) return null;

  const usedWebSearch = didUseWebSearch(request.options);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>LLM Request #{request.id}</span>
          <Chip
            label={request.error ? 'Error' : 'OK'}
            size="small"
            color={request.error ? 'error' : 'success'}
            variant="outlined"
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {request.error && <Alert severity="error">{request.error}</Alert>}

          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Field label="Created" value={new Date(request.createdAt).toLocaleString()} />
            <Field label="Project" value={projectName ?? (request.projectId ? `#${request.projectId}` : '—')} />
            <Field label="User" value={request.userId ? `#${request.userId}` : '—'} />
            <Field label="Provider" value={request.provider} />
            <Field label="Model" value={request.model} />
            <Field label="Model Type" value={request.modelType ?? '—'} />
            <Field
              label="Web Search"
              value={
                usedWebSearch ? (
                  <Chip
                    icon={<TravelExploreIcon sx={{ fontSize: 16 }} />}
                    label="Used"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                ) : (
                  'No'
                )
              }
            />
          </Stack>

          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Field label="Execution time" value={`${request.executionTime} ms`} />
            <Field label="Temperature" value={request.temperature ?? '—'} />
            <Field label="Input tokens" value={request.inputTokens ?? '—'} />
            <Field label="Output tokens" value={request.outputTokens ?? '—'} />
            <Field label="Total tokens" value={request.totalTokens ?? '—'} />
          </Stack>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Sources ({request.citations?.length ?? 0})
            </Typography>
            {request.citations && request.citations.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {request.citations.map((citation, i) => (
                  <Typography key={i} variant="body2">
                    <Link href={citation.url} target="_blank" rel="noopener noreferrer">
                      {citation.title || citation.url}
                    </Link>
                    {citation.publishedDate && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {citation.publishedDate}
                      </Typography>
                    )}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          <PromptBlock label="System prompt" content={request.systemPrompt} copyable />
          <PromptBlock label="Prompt" content={request.prompt} />
          {request.promptResponse && <PromptBlock label="Response" content={request.promptResponse} copyable />}
          {request.options && <PromptBlock label="Options" content={request.options} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
